import { apiClient } from '@littlebird/auth-client';
import {
  useSDK,
  type ProactiveAgentVariant,
  type Routine,
} from '@littlebird/sdk';
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { useOpenChat } from '@/features/chat/use-open-chat';
import {
  cardOwnerChatId,
  withoutHiddenAgentChats,
} from '@/features/home/proactive-agent-visibility';
import { refreshRoutineReads } from '@/features/routines/use-routine-refresh';
import { useTabs } from '@/features/tabs';
import { parseTabRoute } from '@/features/tabs/tab-route';
import { tabStore } from '@/features/tabs/tabs.store';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';

import {
  agentsQueryKey,
  fetchProactiveAgents,
} from './proactive-agent-chat-deletion';
import { createProactiveAgentName } from './proactive-agent-variants';

export type ProactiveAgentState = 'active' | 'paused' | 'stopped';

export type ProactiveAgentInstance = {
  chat_id: string;
  agent: string;
  state: ProactiveAgentState;
  kind?: 'routines' | 'coding' | 'email_triage';
};

export type ProactiveAgents = {
  // Agents the user can start, or use as the base of a new agent
  startable_agents: string[];
  // The agents the app ships with: listed for every user and never deletable
  builtin_agents: string[];
  reserved_agent_names: string[];
  configurations: Record<string, ProactiveAgentVariant>;
  // The agent the server runs as the user's default. Messages sent to the proactive agent reach it
  // while it is one of `default_agent_choices`
  default_agent: string;
  // The agents the user can make their default
  default_agent_choices: string[];
  // Optional while the desktop and backend roll out independently
  selection?: ProactiveAgentSelection;
  selected_agents?: string[];
  instances: ProactiveAgentInstance[];
};

export type ProactiveAgentSelection = {
  follow_default: boolean;
  agents: string[];
};

export const useProactiveAgentsQuery = () => {
  const isInternalUser = useIsInternalUser();
  return useQuery({
    queryKey: agentsQueryKey(),
    queryFn: fetchProactiveAgents,
    enabled: isInternalUser,
  });
};

type SaveProactiveAgentVariant = {
  baseAgent: string;
  promptName: string;
  initialPrompt: string;
};

export const useSaveProactiveAgentVariantMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      baseAgent,
      promptName,
      initialPrompt,
    }: SaveProactiveAgentVariant) => {
      // The new name must not match a built-in agent, a saved agent, or an agent that has a chat. The
      // list is fetched fresh, so names saved or used on another device count too.
      const agents = await queryClient.fetchQuery({
        queryKey: agentsQueryKey(),
        queryFn: fetchProactiveAgents,
      });
      const name = createProactiveAgentName(baseAgent, promptName, [
        ...Object.keys(agents.configurations),
        ...agents.reserved_agent_names,
        ...agents.instances.map((instance) => instance.agent),
      ]);
      // The server adds this agent to the saved ones. It rejects the save if a saved agent already
      // has this name with a different prompt.
      await apiClient.put('/user_configs', {
        config: {
          proactive_agents: {
            variants: {
              [name]: { base_agent: baseAgent, initial_prompt: initialPrompt },
            },
          },
        },
      });
      return name;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: agentsQueryKey() });
      toast.success(
        'Agent saved. Run it from the Agents list when you are ready.'
      );
    },
    onError: () =>
      toast.error(
        'Could not save the new agent. Your prompt is still in the editor. If the name was just taken, try saving again.'
      ),
  });
};

export const useHiddenProactiveAgentChatIds = () => {
  const isInternalUser = useIsInternalUser();
  // Fetched while internal tools are hidden too, which is when every agent chat must be hidden
  const { data } = useQuery({
    queryKey: agentsQueryKey(),
    queryFn: fetchProactiveAgents,
    enabled: userStore.hasInternalAccess,
  });
  const visible = isInternalUser && settingsStore.internalProactiveAgentView;
  return useMemo(
    () => new Set(visible ? [] : data?.instances.map((agent) => agent.chat_id)),
    [data, visible]
  );
};

/** Customer-visible routines, with hidden routines available only in the internal section */
export const useVisibleRoutines = <
  T extends Pick<Routine, 'createdInChatId' | 'hiddenFromUser'>,
>(
  routines: T[],
  { includeHidden = false }: { includeHidden?: boolean } = {}
) => {
  const hiddenChatIds = useHiddenProactiveAgentChatIds();
  const isInternalUser = useIsInternalUser();
  return useMemo(
    () =>
      withoutHiddenAgentChats(
        routines.filter(
          (routine) =>
            !routine.hiddenFromUser || (includeHidden && isInternalUser)
        ),
        hiddenChatIds,
        (routine) => routine.createdInChatId
      ),
    [routines, hiddenChatIds, includeHidden, isInternalUser]
  );
};

/**
 * Returns a setter for whether this device shows proactive agent chats and routines.
 * Hiding them also closes any open tabs of the agents' chats, routines, and routine runs.
 */
export const useInternalProactiveAgentView = (
  instances: readonly ProactiveAgentInstance[]
) => {
  const { closeTab } = useTabs();
  const { reports, routines } = useSDK().stores;
  return (visible: boolean) => {
    settingsStore.setInternalProactiveAgentView(visible);
    if (visible) return;
    const agentChatIds = new Set(instances.map((agent) => agent.chat_id));
    const ownerChatIdOf = (path: string) => {
      const { section, id } = parseTabRoute(path);
      if (!id) return undefined;
      if (section === 'chats') {
        return cardOwnerChatId(
          { chatId: id, origin: 'chat' },
          {
            reportByChatId: (chatId) => reports.getByChatId(chatId),
            routineByReferenceId: (referenceId) =>
              routines.getByReferenceId(referenceId),
          }
        );
      }
      return section === 'routine-chats' || section === 'reports'
        ? routines.getByReferenceId(Number(id))?.createdInChatId
        : undefined;
    };
    for (const tab of [...tabStore.tabs]) {
      const ownerChatId = ownerChatIdOf(tab.route.path);
      if (ownerChatId && agentChatIds.has(ownerChatId)) closeTab(tab.id);
    }
  };
};

type ProactiveAgentAction =
  | { kind: 'start'; agents: string[] }
  | { kind: 'select'; selection: ProactiveAgentSelection }
  | { kind: 'delete-agent' | 'set-default'; agent: string }
  | {
      kind: 'run' | 'delete-chat' | 'pause' | 'resume';
      instance: ProactiveAgentInstance;
    };

const RETRY_HINT = 'If the agent is still replying, stop it, then retry.';

/** Toast text for each action when it succeeds or fails */
const actionCopy: Record<
  ProactiveAgentAction['kind'],
  { done: string; failed: string }
> = {
  start: {
    done: 'Agent started. It works in one chat and creates its own routines.',
    failed: 'Could not start the agent. Refresh the agent list and retry.',
  },
  run: {
    done: 'Proactive agent is reviewing its routines.',
    failed: `Could not start the review. ${RETRY_HINT}`,
  },
  pause: {
    done: 'Agent paused, along with its active routines. Resuming the agent resumes them too.',
    failed: `Could not pause this agent. ${RETRY_HINT}`,
  },
  resume: {
    done: 'Agent resumed, along with the routines that were paused with it.',
    failed: `Could not resume this agent. ${RETRY_HINT}`,
  },
  'delete-chat': {
    done: 'Chat deleted, along with its routines and action cards.',
    failed: `Could not finish deleting this agent's chat and routines. ${RETRY_HINT} Anything already deleted stays deleted.`,
  },
  'delete-agent': {
    done: 'Agent deleted, along with its chat and routines.',
    failed: `Could not finish deleting this agent. ${RETRY_HINT} Anything already deleted stays deleted.`,
  },
  'set-default': {
    done: 'Default agent changed. Other agents keep their current running or paused state.',
    failed: `Could not set this agent as default. ${RETRY_HINT} Otherwise, retry in a minute.`,
  },
  select: {
    done: 'Feed selection updated. No agents were started or paused.',
    failed: 'Could not update Feed selection. Please retry.',
  },
};

/** Sends the action to the server and returns the IDs of the chats to open afterwards */
const requestProactiveAgentAction = async (
  action: ProactiveAgentAction
): Promise<string[]> => {
  switch (action.kind) {
    case 'select':
      await apiClient.put('/proactive_agent/selection', action.selection);
      return [];
    case 'start':
      return apiClient.post<string[]>('/proactive_agent/instances', {
        agents: action.agents,
      });
    case 'delete-agent':
      await apiClient.delete(
        `/proactive_agent/configurations/${encodeURIComponent(action.agent)}`
      );
      return [];
    case 'set-default':
      await apiClient.put('/proactive_agent/default', {
        agent: action.agent,
      });
      return [];
    case 'run':
      return [
        await apiClient.post<string>(
          `/proactive_agent/instances/${action.instance.chat_id}/run`
        ),
      ];
    case 'pause':
    case 'resume':
      await apiClient.post(
        `/proactive_agent/instances/${action.instance.chat_id}/paused?paused=${action.kind === 'pause'}`
      );
      return [];
    case 'delete-chat':
      // The server stops the agent's chat first, so the agent cannot create new routines while
      // its routines are being deleted
      await apiClient.delete(
        `/proactive_agent/instances/${action.instance.chat_id}`
      );
      return [];
  }
};

// Shared by every list of agents, so a change from one list disables the other lists' buttons
const PROACTIVE_AGENT_ACTION_KEY = ['proactive-agent-action'];

/** Whether a change to the user's agents is waiting for the server */
export const useIsChangingProactiveAgents = () =>
  useIsMutating({ mutationKey: PROACTIVE_AGENT_ACTION_KEY }) > 0;

export const useProactiveAgentMutation = () => {
  const sdk = useSDK();
  const queryClient = useQueryClient();
  const { openChat } = useOpenChat();
  return useMutation({
    mutationKey: PROACTIVE_AGENT_ACTION_KEY,
    mutationFn: requestProactiveAgentAction,
    onSuccess: (chatIds, action) => {
      toast.success(actionCopy[action.kind].done);
      const chatsToOpen = settingsStore.internalProactiveAgentView
        ? chatIds
        : [];
      // Not awaited, so a slow sync does not keep the buttons disabled. A new chat must sync to this
      // device before its tab can open, so the tabs open after the sync.
      void (async () => {
        try {
          await sdk.syncNow();
        } catch {
          toast.error(
            'Done, but the change could not sync to this device. Refresh to see it.'
          );
        }
        for (const chatId of chatsToOpen) await openChat(chatId);
      })();
    },
    onError: (_error, action) => toast.error(actionCopy[action.kind].failed),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: agentsQueryKey() });
      refreshRoutineReads();
    },
  });
};
